/**
 * Process-local blockers for build-triggered page reloads.
 *
 * Each live softphone surface acquires one opaque blocker for the lifetime of
 * its call. The app shell can then defer a stale-build reload until every call
 * host in this document is idle, without importing any phone implementation
 * into the bootstrap bundle.
 */

type BuildReloadSafetyListener = () => void;

const blockers = new Set<symbol>();
const listeners = new Set<BuildReloadSafetyListener>();

const notifySafetyChanged = (): void => {
  for (const listener of listeners) listener();
};

export const isBuildReloadSafe = (): boolean => blockers.size === 0;

export const subscribeBuildReloadSafety = (
  listener: BuildReloadSafetyListener,
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Acquire one live-call blocker and return its idempotent release. */
export const acquireBuildReloadBlock = (): (() => void) => {
  const wasSafe = isBuildReloadSafe();
  const blocker = Symbol("build-reload-blocker");
  blockers.add(blocker);
  if (wasSafe) notifySafetyChanged();

  let active = true;
  return () => {
    if (!active) return;
    active = false;
    blockers.delete(blocker);
    if (isBuildReloadSafe()) notifySafetyChanged();
  };
};

export type BuildReloadBlocker = {
  /** Apply the latest live runtime state synchronously. */
  setBlocked: (blocked: boolean) => void;
  /** Clear this source's blocker during teardown. */
  clear: () => void;
};

/**
 * Create an idempotent blocker for an imperative state source.
 *
 * Phone controllers and stores call `setBlocked` before publishing their next
 * React snapshot. This closes the interval between a live call starting and a
 * passive React effect acquiring its blocker.
 */
export const createBuildReloadBlocker = (): BuildReloadBlocker => {
  let release: (() => void) | null = null;

  const setBlocked = (blocked: boolean): void => {
    if (blocked) {
      if (release === null) release = acquireBuildReloadBlock();
      return;
    }
    if (release === null) return;
    release();
    release = null;
  };

  return {
    setBlocked,
    clear: () => setBlocked(false),
  };
};
